
/* 
  Fade in animations. .fade-in-up
*/
document.addEventListener('DOMContentLoaded', () => {
  gsap.registerPlugin(ScrollTrigger);

  gsap.utils.toArray('.fade-in-up').forEach((el) => {
    gsap.from(el, {
      y: 40,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none', 
        once: true
      }
    });
  });

  gsap.utils.toArray('.fade-in-left').forEach((el) => {
    gsap.from(el, {
      x: 40,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none', 
        once: true
      }
    });
  });

  gsap.utils.toArray('.fade-in-right').forEach((el) => {
    gsap.from(el, {
      x: -40,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none', 
        once: true
      }
    });
  });

  gsap.utils.toArray('.fade-rotate-in-up').forEach((el) => {
    gsap.from(el, {
      rotation: 60,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none', 
        once: true
      }
    });
  });
});


/*
This helper function makes a group of elements animate along the x-axis in a seamless, responsive loop.

Features:
 - Uses xPercent so that even if the widths change (like if the window gets resized), it should still work in most cases.
 - When each item animates to the left or right enough, it will loop back to the other side
 - Optionally pass in a config object with values like draggable: true, center: true, speed (default: 1, which travels at roughly 100 pixels per second), paused (boolean), repeat, reversed, and paddingRight.
 - The returned timeline will have the following methods added to it:
   - next() - animates to the next element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
   - previous() - animates to the previous element using a timeline.tweenTo() which it returns. You can pass in a vars object to control duration, easing, etc.
   - toIndex() - pass in a zero-based index value of the element that it should animate to, and optionally pass in a vars object to control duration, easing, etc. Always goes in the shortest direction
   - current() - returns the current index (if an animation is in-progress, it reflects the final index)
   - times - an Array of the times on the timeline where each element hits the "starting" spot.
 */
function horizontalLoop(items, config) {
  let timeline;
  items = gsap.utils.toArray(items);
  config = config || {};
  gsap.context(() => { // use a context so that if this is called from within another context or a gsap.matchMedia(), we can perform proper cleanup like the "resize" event handler on the window
    let onChange = config.onChange,
      lastIndex = 0,
      tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
          let i = tl.closestIndex();
          if (lastIndex !== i) {
            lastIndex = i;
            onChange(items[i], i);
          }
        }, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      spaceBefore = [],
      xPercents = [],
      curIndex = 0,
      indexIsDirty = false,
      center = config.center,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
      timeOffset = 0,
      container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
      totalWidth,
      getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
      populateWidths = () => {
        let b1 = container.getBoundingClientRect(), b2;
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
          b2 = el.getBoundingClientRect();
          spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
          b1 = b2;
        });
        gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
          xPercent: i => xPercents[i]
        });
        totalWidth = getTotalWidth();
      },
      timeWrap,
      populateOffsets = () => {
        timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
        center && times.forEach((t, i) => {
          times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
        });
      },
      getClosest = (values, value, wrap) => {
        let i = values.length,
          closest = 1e10,
          index = 0, d;
        while (i--) {
          d = Math.abs(values[i] - value);
          if (d > wrap / 2) {
            d = wrap - d;
          }
          if (d < closest) {
            closest = d;
            index = i;
          }
        }
        return index;
      },
      populateTimeline = () => {
        let i, item, curX, distanceToStart, distanceToLoop;
        tl.clear();
        for (i = 0; i < length; i++) {
          item = items[i];
          curX = xPercents[i] / 100 * widths[i];
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
          tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
            .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }
        timeWrap = gsap.utils.wrap(0, tl.duration());
      },
      refresh = (deep) => {
        let progress = tl.progress();
        tl.progress(0, true);
        populateWidths();
        deep && populateTimeline();
        populateOffsets();
        deep && tl.draggable && tl.paused() ? tl.time(times[curIndex], true) : tl.progress(progress, true);
      },
      onResize = () => refresh(!(tl.draggable && tl.draggable.isDragging)),
      proxy;
    gsap.set(items, {x: 0});
    populateWidths();
    populateTimeline();
    populateOffsets();
    window.addEventListener("resize", onResize);
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = {time: timeWrap};
      }
      curIndex = newIndex;
      vars.overwrite = true;
      gsap.killTweensOf(proxy);    
      return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.closestIndex = setCurrent => {
      let index = getClosest(times, tl.time(), tl.duration());
      if (setCurrent) {
        curIndex = index;
        indexIsDirty = false;
      }
      return index;
    };
    tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
    tl.next = vars => toIndex(tl.current()+1, vars);
    tl.previous = vars => toIndex(tl.current()-1, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    if (config.draggable && typeof(Draggable) === "function") {
      proxy = document.createElement("div")
      let wrap = gsap.utils.wrap(0, 1),
        ratio, startProgress, draggable, dragSnap, lastSnap, wasPlaying,
        align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
        syncIndex = () => tl.closestIndex(true);
      typeof(InertiaPlugin) === "undefined" && console.warn("InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club");
      draggable = Draggable.create(proxy, {
        trigger: items[0].parentNode,
        type: "x",
        onPressInit() {
          let x = this.x;
          gsap.killTweensOf(tl);
          wasPlaying = !tl.paused();
          tl.pause();
          startProgress = tl.progress();
          refresh();
          ratio = 1 / totalWidth;
          gsap.set(proxy, {x: startProgress / -ratio});
          // fix bug that existed prior to version 3.14.0 that could cause the inertia to jump due to setting the new x position.
          if (+InertiaPlugin.version.split(".")[1] < 14) {
            let tracker = InertiaPlugin.getByTarget(proxy),
                pt = tracker && tracker._props.x;
            pt && (pt.v1 = pt.v2 = startProgress / -ratio); // effectively resets the velocity (only necessary in old versions, before 3.14.0)
          }
        },
        onDrag: align,
        onThrowUpdate: align,
        overshootTolerance: 0,
        inertia: true,
        snap(value) {
          let time = -(value * ratio) * tl.duration(),
            wrappedTime = timeWrap(time),
            snapTime = times[getClosest(times, wrappedTime, tl.duration())],
            dif = snapTime - wrappedTime;
          Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
          lastSnap = (time + dif) / tl.duration() / -ratio;
          return lastSnap;
        },
        onRelease() {
          syncIndex();
          draggable.isThrowing && (indexIsDirty = true);
        },
        onThrowComplete: () => {
          syncIndex();
          wasPlaying && tl.play();
        }
      })[0];
      tl.draggable = draggable;
    }
    tl.closestIndex(true);
    lastIndex = curIndex;
    onChange && onChange(items[curIndex], curIndex);
    timeline = tl;
    return () => window.removeEventListener("resize", onResize); // cleanup
  });
  return timeline;
}




class InfiniteCarousel extends HTMLElement {
  constructor() {
    super();
    this.spacing = 0.1;
    this.duration = 0.5;
    this.playhead = { offset: 0 };
    this.isAnimating = false;
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.cards = gsap.utils.toArray(this.querySelectorAll('.cards li'));
    if (!this.cards.length) return;

    this.init();
  }

  init() {
    gsap.set(this.cards, { xPercent: 400, opacity: 1, scale: 1 });

    this.seamlessLoop = this.buildSeamlessLoop(
      this.cards,
      this.spacing,
      this.animateCard.bind(this)
    );

    this.wrapTime = gsap.utils.wrap(0, this.seamlessLoop.duration());

    this.scrub = gsap.to(this.playhead, {
      offset: 0,
      duration: this.duration,
      ease: "power3",
      paused: true,
      onUpdate: () => {
        this.seamlessLoop.time(this.wrapTime(this.playhead.offset));
      }
    });

    this.bindEvents();

    // Set initial active card without auto-playing
    this.setActiveCard(this.cards[0]);
  }

  animateCard(element) {
    const tl = gsap.timeline();

    // Horizontal movement
    tl.fromTo(
      element,
      { xPercent: 400 },
      {
        xPercent: -400,
        duration: 1,
        ease: "none",
        immediateRender: false
      },
      0
    );

    // Linear scale: smaller at edges, larger at center
    // At xPercent 400 (right): scale 0.6
    // At xPercent 0 (center): scale 1.0
    // At xPercent -400 (left): scale 0.6
    tl.fromTo(
      element,
      { scale: 0.6 },
      {
        scale: 1.0,
        duration: 0.5,
        ease: "none",
        immediateRender: false
      },
      0
    ).to(
      element,
      {
        scale: 0.6,
        duration: 0.5,
        ease: "none"
      },
      0.5
    );

    // Z-index: higher at center
    tl.fromTo(
      element,
      { zIndex: 1 },
      {
        zIndex: 100,
        duration: 0.5,
        ease: "none",
        immediateRender: false
      },
      0
    ).to(
      element,
      {
        zIndex: 1,
        duration: 0.5,
        ease: "none"
      },
      0.5
    );

    // Opacity: only show cards within index 2 from center (5 cards total)
    // Cards should be hidden until xPercent ~200 and after xPercent ~-200
    tl.set(element, { opacity: 0 }, 0)
      .to(element, { opacity: 1, duration: 0.05, ease: "none" }, 0.25)
      .to(element, { opacity: 0, duration: 0.05, ease: "none" }, 0.70);

    return tl;
  }

  goToOffset(offset) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    this.scrub.vars.offset = offset;
    this.scrub.vars.onComplete = () => {
      this.isAnimating = false;
      this.playCardVideo(this.cards[this.currentIndex]);
    };
    this.scrub.invalidate().restart();
  }

  next() {
    if (!this.isAnimating) {
      this.currentIndex = (this.currentIndex + 1) % this.cards.length;
      this.goToOffset(this.playhead.offset + this.spacing);
    }
  }

  prev() {
    if (!this.isAnimating) {
      this.currentIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
      this.goToOffset(this.playhead.offset - this.spacing);
    }
  }

  bindEvents() {
    const nextBtn = this.querySelector(".next");
    const prevBtn = this.querySelector(".prev");

    if (nextBtn) nextBtn.addEventListener("click", () => this.next());
    if (prevBtn) prevBtn.addEventListener("click", () => this.prev());

    // Setup video controls
    this.setupVideoControls();
    
    // Setup card click navigation
    this.setupCardNavigation();
  }

  setupVideoControls() {
    const videoCards = this.querySelectorAll('.video-card');
    
    videoCards.forEach(card => {
      const video = card.querySelector('video');
      const playPauseBtn = card.querySelector('.video-play-pause');
      
      if (!video || !playPauseBtn) return;
      
      const playIcon = playPauseBtn.querySelector('.play-icon');
      const pauseIcon = playPauseBtn.querySelector('.pause-icon');
      
      card.togglePlayPause = () => {
        if (video.paused) {
          video.play();
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
          card.classList.add('playing');
        } else {
          video.pause();
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
          card.classList.remove('playing');
        }
      };
      
      card.playVideo = () => {
        video.play();
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
        card.classList.add('playing');
      };
      
      card.pauseVideo = () => {
        video.pause();
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
        card.classList.remove('playing');
      };
      
      playPauseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        card.togglePlayPause();
      });
      
      // Reset to play icon when video ends
      video.addEventListener('ended', () => {
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
        card.classList.remove('playing');
      });
    });
  }

  setupCardNavigation() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Don't navigate if clicking the play/pause button
        const playPauseBtn = card.querySelector('.video-play-pause');
        if (playPauseBtn && playPauseBtn.contains(e.target)) {
          return;
        }
        
        // Calculate the target offset for this card
        const targetOffset = index * this.spacing;
        
        // Set current index and navigate (onComplete handles playCardVideo)
        this.currentIndex = index;
        this.goToOffset(targetOffset);
      });
    });
  }

  setActiveCard(activeCard) {
    this.cards.forEach(card => card.classList.remove('is-active'));
    activeCard.classList.add('is-active');
  }

  playCardVideo(activeCard) {
    // Pause all videos first and remove active state
    this.cards.forEach(card => {
      if (card.pauseVideo) {
        card.pauseVideo();
      }
      card.classList.remove('is-active');
    });
    
    // Play the active card's video and mark it active
    if (activeCard.playVideo) {
      activeCard.playVideo();
    }
    activeCard.classList.add('is-active');
  }

  buildSeamlessLoop(items, spacing, animateFunc) {
    let overlap = Math.ceil(1 / spacing),
        startTime = items.length * spacing + 0.5,
        loopTime = (items.length + overlap) * spacing + 1,
        rawSequence = gsap.timeline({ paused: true }),
        seamlessLoop = gsap.timeline({
          paused: true,
          repeat: -1
        }),
        l = items.length + overlap * 2,
        time, i, index;

    for (i = 0; i < l; i++) {
      index = i % items.length;
      time = i * spacing;
      rawSequence.add(animateFunc(items[index]), time);
    }

    rawSequence.time(startTime);

    seamlessLoop
      .to(rawSequence, {
        time: loopTime,
        duration: loopTime - startTime,
        ease: "none"
      })
      .fromTo(
        rawSequence,
        { time: overlap * spacing + 1 },
        {
          time: startTime,
          duration: startTime - (overlap * spacing + 1),
          immediateRender: false,
          ease: "none"
        }
      );

    return seamlessLoop;
  }
}

customElements.define('infinite-carousel', InfiniteCarousel);